import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Share2, ArrowRight, Trophy, UserPlus, Copy } from "lucide-react";
import { API_BASE } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";

export default function RevealModal({ open, onClose, puzzleId, mode, won, score, history, revealData, number, onShare, onNext }) {
  const [imgUrl, setImgUrl] = useState(null);
  const { user } = useAuth();
  const soundcloudTrackId = revealData?.soundcloud_track_id;
  const soundcloudUrl = revealData?.soundcloud_url;

  useEffect(() => {
    if (open && revealData?.reveal_image_available && puzzleId) {
      setImgUrl(`${API_BASE}/media/image/${puzzleId}`);
    }
  }, [open, puzzleId, revealData]);

  if (!revealData) return null;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        className="max-w-md bg-slate-900 border-2 border-red-600/60 text-white p-0 overflow-hidden max-h-[90vh] overflow-y-auto"
        data-testid="reveal-modal">
        {won && <ConfettiBurst />}
        <DialogHeader className="p-5 pb-2 text-center">
          <div className="mx-auto mb-2 h-10 w-10 rounded-full bg-red-600/20 flex items-center justify-center bell">
            <Trophy className="h-5 w-5 text-red-500" />
          </div>
          <DialogTitle className="font-display text-3xl font-black uppercase tracking-tight">
            {won ? "Winner!" : "Good try"}
          </DialogTitle>
          <p className="text-xs font-mono uppercase tracking-widest text-red-500">
            POPLeSS #{number} · {mode === "theme" ? "Entrance Theme" : "Promo"}
          </p>
        </DialogHeader>

        {imgUrl && (
          <div className="mx-5 mb-3 rounded-xl overflow-hidden border border-slate-800">
            <img src={imgUrl} alt="Reveal" className="w-full max-h-56 object-cover"
                 onError={() => setImgUrl(null)} data-testid="reveal-image" />
          </div>
        )}

        <div className="px-5 space-y-3">
          <div className="text-center">
            <div className="text-xs uppercase tracking-wider text-slate-400 font-mono">Answer</div>
            <div className="font-display text-2xl font-black text-white" data-testid="reveal-answer">
              {revealData.answer}
            </div>
            <div className="flex justify-center gap-2 mt-1 flex-wrap">
              {revealData.era && (
                <span className="text-[10px] font-mono uppercase px-2 py-0.5 rounded bg-slate-800 border border-slate-700 text-slate-300">
                  {revealData.era}
                </span>
              )}
              {revealData.year && (
                <span className="text-[10px] font-mono uppercase px-2 py-0.5 rounded bg-slate-800 border border-slate-700 text-slate-300">
                  {revealData.year}
                </span>
              )}
              {revealData.event && (
                <span className="text-[10px] font-mono uppercase px-2 py-0.5 rounded bg-slate-800 border border-slate-700 text-slate-300">
                  {revealData.event}
                </span>
              )}
            </div>
          </div>

          {/* Post-reveal audio playback — full controls now that the puzzle has ended. */}
          {revealData.audio_path && puzzleId && (
            <audio controls src={`${API_BASE}/media/audio/${puzzleId}`} preload="none"
                   className="w-full mt-2" data-testid="reveal-audio-full" />
          )}
          {revealData.song_title && (
            <div className="text-center text-xs font-mono uppercase tracking-widest text-slate-400"
                 data-testid="reveal-song-title">
              Song: {revealData.song_title}{revealData.artist ? ` · ${revealData.artist}` : ""}
            </div>
          )}
          {soundcloudTrackId && (
            <div>
              <iframe title="Full SoundCloud track"
                      scrolling="no" frameBorder="0" allow="autoplay; encrypted-media"
                      className="w-full h-[300px] rounded-lg border border-slate-800"
                      data-testid="reveal-sc-iframe"
                      src={`https://w.soundcloud.com/player/?url=${encodeURIComponent(`https://api.soundcloud.com/tracks/${soundcloudTrackId}`)}&auto_play=false&hide_related=true&show_user=true&visual=true&color=%23dc2626`} />
              {soundcloudUrl && (
                <a href={soundcloudUrl} target="_blank" rel="noopener noreferrer"
                   className="text-[10px] font-mono uppercase tracking-widest text-orange-400 hover:text-orange-300 block mt-1"
                   data-testid="reveal-sc-link">
                  View on SoundCloud →
                </a>
              )}
            </div>
          )}

          {revealData.description && (
            <p className="text-sm text-slate-300 leading-relaxed text-center italic border-t border-slate-800 pt-3"
               data-testid="reveal-description">
              "{revealData.description}"
            </p>
          )}

          <div className="grid grid-cols-2 gap-3 text-center border-t border-slate-800 pt-3">
            <Stat label="Points" value={score} testid="reveal-score" />
            <Stat label="Attempts" value={history.filter((h) => !h.skipped).length} testid="reveal-attempts" />
          </div>

          <div className="flex justify-center gap-1 pt-2">
            {[0, 1, 2, 3, 4, 5].map((i) => {
              const h = history[i];
              let cls = "bg-slate-800";
              if (h?.correct) cls = "bg-emerald-500";
              else if (h?.skipped) cls = "bg-amber-500";
              else if (h) cls = "bg-red-600";
              return <span key={i} className={`h-5 w-5 rounded-sm ${cls}`} data-testid={`reveal-square-${i}`} />;
            })}
          </div>

          {/* Fan referral tile — invite a friend after seeing the result */}
          <ReferralTile signedIn={!!user} number={number} score={score} />
        </div>

        <div className="p-5 pt-4 space-y-2">
          <Button onClick={onShare} data-testid="share-results-btn"
            className="w-full h-11 bg-gradient-to-r from-red-600 to-red-700 hover:from-red-500 hover:to-red-600 text-white font-extrabold uppercase tracking-wider">
            <Share2 className="h-4 w-4 mr-2" /> Share Result
          </Button>
          {onNext && (
            <Button variant="outline" onClick={onNext} data-testid="play-next-btn"
              className="w-full h-11 border-slate-700 text-slate-200 hover:bg-slate-800 font-bold uppercase tracking-wider">
              Play Next Mode <ArrowRight className="h-4 w-4 ml-2" />
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function Stat({ label, value, testid }) {
  return (
    <div>
      <div className="text-xs uppercase tracking-wider text-slate-400 font-mono">{label}</div>
      <div className="font-mono text-2xl font-black text-white" data-testid={testid}>{value}</div>
    </div>
  );
}

function ConfettiBurst() {
  const items = Array.from({ length: 40 });
  return (
    <div className="pointer-events-none absolute inset-0 overflow-hidden z-10">
      {items.map((_, i) => {
        const colors = ["#DC2626", "#EF4444", "#F8FAFC", "#CBD5E1", "#F59E0B"];
        const c = colors[i % colors.length];
        const left = Math.random() * 100;
        const delay = Math.random() * 0.6;
        const size = 4 + Math.random() * 6;
        return (
          <span key={i} className="spark absolute rounded-sm"
            style={{
              left: `${left}%`, top: "-8%",
              width: `${size}px`, height: `${size * 1.6}px`,
              background: c, animationDelay: `${delay}s`,
            }} />
        );
      })}
    </div>
  );
}


function ReferralTile({ signedIn, number, score }) {
  const link = typeof window !== "undefined" ? window.location.origin : "";
  const copy = () => {
    const cta = signedIn
      ? `I just scored ${score} on POPLeSS #${number}. Think you can beat me?`
      : `Try today's POPLeSS wrestling audio game — 1 puzzle, 6 windows, 10 seconds.`;
    const text = `${cta}\n${link}`;
    if (navigator.share) {
      navigator.share({ text }).catch(() => {});
    } else {
      navigator.clipboard.writeText(text);
      toast.success("Invite copied to clipboard!");
    }
  };
  return (
    <div className="mt-3 p-3 rounded-xl border border-red-600/40 bg-gradient-to-br from-red-950/30 to-slate-900/60 flex items-center gap-3"
         data-testid="referral-tile">
      <div className="h-9 w-9 rounded-full bg-red-600/20 flex items-center justify-center flex-shrink-0">
        <UserPlus className="h-4 w-4 text-red-500" />
      </div>
      <div className="min-w-0 flex-1">
        <div className="font-display font-bold uppercase text-sm leading-tight">Invite a friend</div>
        <div className="text-[11px] text-slate-400 leading-snug">
          {signedIn ? "Challenge them to beat your score." : "Share the arena — no signup needed to play."}
        </div>
      </div>
      <button onClick={copy} data-testid="referral-copy-btn"
        className="text-xs font-mono uppercase tracking-widest text-red-500 hover:text-red-400 flex items-center gap-1 flex-shrink-0">
        <Copy className="h-3 w-3" /> Copy
      </button>
    </div>
  );
}
